import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, Plus, Edit, Trash2, Users, UserPlus, Crown,
  Shield, User, MoreVertical, X
} from 'lucide-react';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleIcons = { owner: Crown, admin: Shield, member: User };
const roleColors = { owner: 'text-[#FF9500]', admin: 'text-[#002FA7]', member: 'text-[#4B5563]' };
const roleBadgeColors = { owner: 'bg-[#FF9500] text-white', admin: 'bg-[#002FA7] text-white', member: 'bg-[#E5E7EB] text-[#4B5563]' };

const TeamsPage = () => {
  const { token, user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [memberForm, setMemberForm] = useState({ user_id: '', role: 'member' });

  useEffect(() => { fetchTeams(); fetchUsers(); }, []);

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`, {
        params: { search },
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(res.data);
    } catch (e) { toast.error('Failed to fetch teams'); }
    finally { setLoading(false); }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data);
    } catch (e) { /* ignore */ }
  };

  const handleSave = async () => {
    if (!form.name) return toast.error('Team name required');
    try {
      if (editMode && selectedTeam) {
        await axios.put(`${API}/teams/${selectedTeam.team_id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team updated');
      } else {
        await axios.post(`${API}/teams`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Team created');
      }
      setIsCreateOpen(false);
      setForm({ name: '', description: '' });
      setEditMode(false);
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save team'); }
  };

  const handleDelete = async (teamId) => {
    if (!confirm('Delete this team? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/teams/${teamId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Team deleted');
      if (selectedTeam?.team_id === teamId) { setIsViewOpen(false); setSelectedTeam(null); }
      fetchTeams();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete team'); }
  };

  const handleAddMember = async () => {
    if (!memberForm.user_id) return toast.error('Select a user');
    try {
      await axios.post(`${API}/teams/${selectedTeam.team_id}/members`, memberForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member added');
      setIsAddMemberOpen(false);
      setMemberForm({ user_id: '', role: 'member' });
      refreshSelectedTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add member'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Remove this member from the team?')) return;
    try {
      await axios.delete(`${API}/teams/${selectedTeam.team_id}/members/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member removed');
      refreshSelectedTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove member'); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API}/teams/${selectedTeam.team_id}/members/${userId}/role`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role updated');
      refreshSelectedTeam();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const refreshSelectedTeam = async () => {
    try {
      const res = await axios.get(`${API}/teams/${selectedTeam.team_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTeam(res.data);
      fetchTeams();
    } catch (e) { /* ignore */ }
  };

  const openEdit = (team) => {
    setSelectedTeam(team);
    setForm({ name: team.name, description: team.description || '' });
    setEditMode(true);
    setIsCreateOpen(true);
  };

  const openView = (team) => {
    setSelectedTeam(team);
    setIsViewOpen(true);
  };

  const availableUsers = selectedTeam
    ? allUsers.filter(u => !selectedTeam.members?.some(m => m.user_id === u.user_id))
    : allUsers;

  const filteredTeams = teams.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="teams-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Teams</h1>
          <p className="text-[#4B5563] text-sm">Create and manage teams</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => { setEditMode(false); setForm({ name: '', description: '' }); setIsCreateOpen(true); }} data-testid="create-team-btn">
          <Plus className="w-4 h-4 mr-2" />New Team
        </Button>
      </div>

      {/* Search */}
      <Card className="border-[#E5E7EB] mb-6">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input placeholder="Search teams..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="team-search" />
          </div>
        </CardContent>
      </Card>

      {/* Teams Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : filteredTeams.length === 0 ? (
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-12 text-center text-[#9CA3AF]">
            <Users className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
            <p>No teams yet. Create your first team to get started.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeams.map((team) => (
            <Card key={team.team_id} className="border-[#E5E7EB] hover:border-[#002FA7]/30 transition-colors cursor-pointer" onClick={() => openView(team)} data-testid={`team-card-${team.team_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center rounded">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A0A0A]">{team.name}</h3>
                      <p className="text-xs text-[#9CA3AF]">{team.members?.length || 0} members</p>
                    </div>
                  </div>
                  <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(team)} data-testid={`edit-team-${team.team_id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[#FF3B30]" onClick={() => handleDelete(team.team_id)} data-testid={`delete-team-${team.team_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                {team.description && <p className="text-sm text-[#4B5563] mb-3 line-clamp-2">{team.description}</p>}
                {/* Member avatars */}
                <div className="flex items-center gap-1">
                  {team.members?.slice(0, 5).map((m) => (
                    <div key={m.user_id} className="w-7 h-7 bg-[#F3F4F6] border-2 border-white rounded-full flex items-center justify-center text-[10px] font-medium text-[#4B5563]" title={m.name}>
                      {m.name?.charAt(0)?.toUpperCase()}
                    </div>
                  ))}
                  {(team.members?.length || 0) > 5 && (
                    <div className="w-7 h-7 bg-[#E5E7EB] border-2 border-white rounded-full flex items-center justify-center text-[10px] font-medium text-[#4B5563]">
                      +{team.members.length - 5}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Team Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="team-dialog">
          <DialogHeader><DialogTitle>{editMode ? 'Edit' : 'Create'} Team</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Team Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Engineering" data-testid="team-name-input" /></div>
            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What does this team do?" data-testid="team-description-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleSave} data-testid="save-team-btn">{editMode ? 'Update' : 'Create'} Team</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Team Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-xl" data-testid="view-team-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#002FA7]" />
              {selectedTeam?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedTeam && (
            <div className="space-y-4">
              {selectedTeam.description && <p className="text-sm text-[#4B5563]">{selectedTeam.description}</p>}

              <div className="flex items-center justify-between">
                <h4 className="font-medium text-sm">Members ({selectedTeam.members?.length || 0})</h4>
                <Button size="sm" variant="outline" onClick={() => { setMemberForm({ user_id: '', role: 'member' }); setIsAddMemberOpen(true); }} data-testid="add-member-btn">
                  <UserPlus className="w-3.5 h-3.5 mr-1" />Add Member
                </Button>
              </div>

              <ScrollArea className="max-h-64">
                <div className="space-y-2">
                  {selectedTeam.members?.map((member) => {
                    const RoleIcon = roleIcons[member.role] || User;
                    return (
                      <div key={member.user_id} className="flex items-center justify-between p-3 bg-[#F9FAFB] rounded" data-testid={`member-${member.user_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-[#E5E7EB] rounded-full flex items-center justify-center text-xs font-medium">
                            {member.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium">{member.name}</p>
                            <p className="text-xs text-[#9CA3AF]">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={roleBadgeColors[member.role]}>
                            <RoleIcon className={`w-3 h-3 mr-1`} />{member.role}
                          </Badge>
                          {member.role !== 'owner' && (
                            <div className="flex gap-1">
                              <Select value={member.role} onValueChange={(v) => handleRoleChange(member.user_id, v)}>
                                <SelectTrigger className="h-7 w-24 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="admin">Admin</SelectItem>
                                  <SelectItem value="member">Member</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-[#FF3B30]" onClick={() => handleRemoveMember(member.user_id)}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>

              <p className="text-xs text-[#9CA3AF]">Created {format(new Date(selectedTeam.created_at), 'MMM d, yyyy')}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={isAddMemberOpen} onOpenChange={setIsAddMemberOpen}>
        <DialogContent data-testid="add-member-dialog">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>User *</Label>
              <Select value={memberForm.user_id} onValueChange={(v) => setMemberForm({ ...memberForm, user_id: v })}>
                <SelectTrigger data-testid="member-user-select"><SelectValue placeholder="Select user" /></SelectTrigger>
                <SelectContent>
                  {availableUsers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.email})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={memberForm.role} onValueChange={(v) => setMemberForm({ ...memberForm, role: v })}>
                <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="member">Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddMemberOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleAddMember} data-testid="confirm-add-member-btn">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamsPage;
